/**
 * Typesense category value → /category/<slug> route segment.
 *
 * Why this exists
 * ---------------
 * The two vocabularies are not the same, and lowercasing one does not produce
 * the other:
 *
 *   Typesense `category_type`   CARTRIDGE   EXTRACT        PREROLL    EDIBLE
 *   Route slug (nav, sitemap)   vapes       concentrates   prerolls   edibles
 *
 * Both spellings currently resolve — `/category/cartridge` renders the same
 * products as `/category/vapes` — but only the nav slug has optimised metadata.
 * The raw-value pages fall through to the generic `default` title and each
 * self-canonicalises, so they are duplicates of the real category pages rather
 * than aliases of them. Anything linking to `/category/cartridge` therefore
 * feeds a duplicate URL instead of the page meant to rank.
 *
 * The product breadcrumb did exactly that, via `category.toLowerCase()`. Use
 * `categoryPath()` for every category link so there is one URL per category.
 *
 * Verified against the live `category_type` facet (10 values, all mapped) and
 * the slugs used by the header nav, mobile sidebar and category menu.
 */

/**
 * Every live `category_type` value. Keys are compared case-insensitively, so a
 * feed that starts sending "Cartridge" instead of "CARTRIDGE" still resolves.
 *
 * PILL maps to itself: the nav links `/category/pill` and no friendlier slug
 * exists, though that page has no optimised metadata yet.
 */
const CATEGORY_SLUGS: Record<string, string> = {
  BEVERAGE: 'beverages',
  CARTRIDGE: 'vapes',
  EDIBLE: 'edibles',
  EXTRACT: 'concentrates',
  FLOWER: 'flower',
  MERCH: 'merch',
  PILL: 'pill',
  PREROLL: 'prerolls',
  TINCTURE: 'tinctures',
  TOPICAL: 'topicals',
};

/**
 * Route slug for a category, or null when we have no page for it.
 *
 * Returns null rather than guessing — an unmapped category getting
 * `toLowerCase()` is how the duplicate URLs appeared in the first place.
 * Callers should render plain text when this is null.
 */
export function toCategorySlug(category: string | null | undefined): string | null {
  const key = (category ?? '').trim().toUpperCase();
  return CATEGORY_SLUGS[key] ?? null;
}

/** `/category/<slug>` for a category, or null when it has no page. */
export function categoryPath(category: string | null | undefined): string | null {
  const slug = toCategorySlug(category);
  return slug ? `/category/${slug}` : null;
}

/**
 * Whether this path segment names a category we actually publish.
 *
 * Accepts both vocabularies — the route slug ("vapes") and the raw
 * `category_type` it maps from ("CARTRIDGE") — because both currently resolve
 * and the raw form is 301'd to the canonical slug rather than 404'd.
 *
 * The category route matches `$slug`, so without this every invented word
 * rendered a full page: `/category/shop` returned 200 and was crawled as a thin
 * duplicate of the real category pages.
 */
export function isKnownCategory(value: string | null | undefined): boolean {
  return normaliseCategory(value) !== null;
}

/**
 * Normalise either a `category_type` value ("PREROLL") or a route slug
 * ("prerolls") to the route slug. Null when it is neither.
 */
function normaliseCategory(value: string | null | undefined): string | null {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return null;
  if (Object.values(CATEGORY_SLUGS).includes(v)) return v;
  return toCategorySlug(v);
}

/**
 * Whether a subtype should be offered as a filter option.
 *
 * `subtype` and `category_type` are separate Typesense fields with overlapping
 * vocabularies, so a product can be `category_type: PREROLL, subtype: FLOWER`
 * — a plain flower pre-roll, as opposed to INFUSED or BLUNT. Embarc lists
 * exactly this under Prerolls; it is real data, not a mistake.
 *
 * Catalyst used to hide any subtype whose name matched a category, everywhere,
 * via a list repeated in five files. Measured against all 34 store collections,
 * that list did almost nothing: thirteen of its fourteen values match no
 * product at all, and the fourteenth — FLOWER, 2,560 products — sits under
 * PREROLL every single time and never under FLOWER. So the rule only ever
 * suppressed the one case where the subtype was meaningful.
 *
 * It also had a side effect worth knowing: the filter menu clears any `subtype`
 * it cannot find among the offered options, so `?subtype=FLOWER` applied and
 * was immediately stripped, bouncing the URL back to the unfiltered category.
 * Anyone who reached that filter got silently redirected.
 *
 * The rule is now scoped to what it was presumably for: a subtype is hidden
 * only when it names the category being browsed, where it would be a redundant
 * "Flower › Flower". Elsewhere it is a real distinction and is offered.
 *
 * Note this is deliberately still a guard rather than nothing at all. No
 * product currently pairs FLOWER with FLOWER, but Typesense holds only live
 * inventory, so that is a fact about today rather than a guarantee.
 *
 * @param currentCategory the category being browsed, as a `category_type` value
 *   or route slug. Omit where several categories are shown at once (shop,
 *   brand, deals) — there is no single category for a subtype to duplicate.
 */
export function isFilterableSubtype(
  subtype: string | null | undefined,
  currentCategory?: string | null,
): boolean {
  const value = (subtype ?? '').trim();
  if (!value) return false;
  const subtypeAsCategory = normaliseCategory(value);
  // Not a category name at all — nothing to collide with.
  if (!subtypeAsCategory) return true;
  const browsing = normaliseCategory(currentCategory);
  return browsing !== subtypeAsCategory;
}

/**
 * The category page filtered to one subtype.
 *
 * The `subtype` param takes the raw Typesense facet value ("510 THREAD",
 * "ALL-IN-ONE") and matches case-insensitively, so the product's own value can
 * be passed straight through.
 *
 * Null when the category has no page — a subtype has nowhere to live on its own
 * — or when the subtype names that same category, since the filter menu will
 * not offer it there and would clear it from the URL on arrival.
 */
export function subtypePath(
  category: string | null | undefined,
  subtype: string | null | undefined,
): string | null {
  const base = categoryPath(category);
  const value = (subtype ?? '').trim();
  if (!base || !value || !isFilterableSubtype(value, category)) return null;
  return `${base}?subtype=${encodeURIComponent(value)}`;
}
